/* global Office */
import React, { useState, useEffect } from "react";
import { LoginUser } from "../auth/authService";
import { VerificationService } from "../services/verificationService";
import { TemplateService } from "../services/templateService";
import { UploadService } from "../services/uploadService";
import {
  IoAlertCircleOutline,
  IoArrowBackOutline,
  IoShieldOutline,
  IoAttachOutline,
} from "react-icons/io5";
import { Session } from "../auth/session";
import { RemoteLogger } from "../services/remoteLogger";
import SelectVerifier from "./SelectVerifier";
import { Header } from "./Header";

interface VerifyEmailProps {
  onBack: () => void;
  user: LoginUser | null;
  onLogout: () => void;
  onViewHistory: () => void;
}

interface AttachmentDetails {
  id: string;
  name: string;
  size: number;
  attachmentType: string;
  isInline: boolean;
}

interface EmailDetails {
  subject: string;
  sender: string;
  senderAddress: string;
  senderDomain: string;
  bodyPreview: string;
  attachments: AttachmentDetails[];
}

const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

const VerifyEmailView: React.FC<VerifyEmailProps> = ({ user, onBack, onLogout, onViewHistory }) => {
  const [emailDetails, setEmailDetails] = useState<EmailDetails | null>(null);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [status, setStatus] = useState<
    "none" | "select-verifier" | "loading" | "requested" | "verified"
  >("none");
  const [lastVerificationId, setLastVerificationId] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date().toISOString());
  const loadEmailDetails = () => {
    setLoadingEmail(true);
    try {
      const item = Office.context?.mailbox?.item;
      if (!item) {
        RemoteLogger.warn("loadEmailDetails: No active email item found.");
        setEmailDetails(null);
        setLoadingEmail(false);
        return;
      }

      const date = new Date();
      const formattedDate = date.toISOString();
      RemoteLogger.info(Office.context?.mailbox?.item.subject, " 222");
      RemoteLogger.info(
        "loadEmailDetails: Active item subject: " + formattedDate + "-" + item.subject
      );
      RemoteLogger.info("loadEmailDetails: Active item sender: " + item.from?.emailAddress);
      const subject = item.subject || "No Subject";
      const sender = item.from?.displayName || "Unknown Sender";
      const senderAddress = item.from?.emailAddress || "";
      const senderDomain = senderAddress.includes("@") ? senderAddress.split("@")[1] : "";

      const attachments: AttachmentDetails[] = [];
      if (item.attachments && Array.isArray(item.attachments)) {
        item.attachments.forEach((att: any) => {
          attachments.push({
            id: att.id,
            name: att.name,
            size: att.size,
            attachmentType: att.attachmentType,
            isInline: att.isInline,
          });
        });
      }

      item.body.getAsync(Office.CoercionType.Text, (result) => {
        const body =
          result.status === Office.AsyncResultStatus.Succeeded
            ? result.value
            : "Could not load body preview.";
        const preview = body.length > 200 ? body.substring(0, 200) + "..." : body;
        setEmailDetails({
          subject,
          sender,
          senderAddress,
          senderDomain,
          bodyPreview: preview,
          attachments,
        });
        setLoadingEmail(false);
      });
    } catch {
      setEmailDetails(null);
      setLoadingEmail(false);
    }
  };

  const getAttachmentBase64 = (attachmentId: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      Office.context.mailbox.item.getAttachmentContentAsync(attachmentId, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value.content); // already base64
        } else {
          reject(result.error);
        }
      });
    });
  };

  const handleRequestVerification = async (
    verifierIds: number[],
    customQuestion?: string,
    organizationId?: number
  ) => {
    setErrorMessage(null);
    setStatus("loading");
    try {
      const token = await Session.getToken();
      if (!token) {
        RemoteLogger.warn("No token found during request verification, logging out...");
        onLogout();
        return;
      }

      const templateList = await TemplateService.listAll(token);

      if (templateList.status === 401) {
        RemoteLogger.warn("Session expired (401) during template retrieval, logging out...");
        onLogout();
        return;
      }

      const templateId = templateList.data?.data?.templateId;
      if (!templateId) {
        setErrorMessage("Could not retrieve template ID.");
        setStatus("none");
        return;
      }

      // Fetch template details by ID as requested and log the response
      const templateDetail = await TemplateService.getById(templateId, token);
      RemoteLogger.info("Fetched Template Details in VerifyEmailView:", templateDetail);

      if (templateDetail.status === 401) {
        RemoteLogger.warn(
          "Session expired (401) during template detail retrieval, logging out..."
        );
        onLogout();
        return;
      }

      const templateFields = templateDetail.data?.data?.fields || [];
      const templateName = templateDetail.data?.data?.name || "Email Verification";
      const templateDescription =
        templateDetail.data?.data?.description ||
        `Automatic verification for email: ${emailDetails?.subject}`;

      // Dynamically resolve field IDs by their keys
      const senderAddressField = templateFields.find((f: any) => (f.inputField?.key || f.key) === "sender_address");
      const domainField = templateFields.find((f: any) => (f.inputField?.key || f.key) === "domain");
      const subjectLineField = templateFields.find((f: any) => (f.inputField?.key || f.key) === "subject_line");

      const inputFieldValues = [];

      if (senderAddressField) {
        inputFieldValues.push({
          inputFieldId: senderAddressField.inputField?.id || senderAddressField.id,
          value: emailDetails?.senderAddress || emailDetails?.sender || "",
        });
      }
      if (domainField) {
        inputFieldValues.push({
          inputFieldId: domainField.inputField?.id || domainField.id,
          value: emailDetails?.senderDomain || "",
        });
      }
      if (subjectLineField) {
        inputFieldValues.push({
          inputFieldId: subjectLineField.inputField?.id || subjectLineField.id,
          value: emailDetails?.subject || "",
        });
      }

      // Fallback if dynamic resolution didn't find any fields
      if (inputFieldValues.length === 0) {
        inputFieldValues.push(
          {
            inputFieldId: 479,
            value: emailDetails?.senderAddress || emailDetails?.sender || "",
          },
          {
            inputFieldId: 480,
            value: emailDetails?.senderDomain || "",
          },
          {
            inputFieldId: 481,
            value: emailDetails?.subject || "",
          }
        );
      }

      const attachmentsField = templateFields.find(
        (f: any) =>
          (f.inputField?.key || f.key) === "attachments" ||
          (f.inputField?.key || f.key) === "files" ||
          (f.inputField?.key || f.key) === "attachment_list" ||
          (f.inputField?.key || f.key) === "attachment"
      );

      // Upload files to S3 via Flow 2 and add mediaId UUIDs to inputFieldValues
      if (emailDetails?.attachments && emailDetails.attachments.length > 0 && attachmentsField) {
        const fieldId = attachmentsField.inputField?.id || attachmentsField.id || 642;
        for (const att of emailDetails.attachments) {
          try {
            const content = await getAttachmentBase64(att.id);
            const mimeType = att.attachmentType || "application/octet-stream";
            const uploadResult = await UploadService.uploadAttachmentFlow2(
              att.name,
              mimeType,
              content,
              templateId,
              fieldId,
              token
            );
            if (uploadResult.success && uploadResult.uuid) {
              inputFieldValues.push({
                inputFieldId: fieldId,
                mediaId: uploadResult.uuid,
              });
            } else {
              RemoteLogger.error(`Failed to upload attachment ${att.name} via Flow 2: ${uploadResult.error}`);
            }
          } catch (e) {
            RemoteLogger.error(`Error processing attachment ${att.name} via Flow 2:`, e);
          }
        }
      }

      const finalDescription = customQuestion || templateDescription;

      // The template's "title" field is mandatory and becomes the verification title
      const titleField = templateFields.find((f: any) => (f.inputField?.key || f.key) === "title");
      if (titleField) {
        inputFieldValues.push({
          inputFieldId: titleField.inputField?.id || titleField.id,
          value: templateName,
        });
      }

      const payload = {
        templateId: templateId,
        title: templateName,
        description: finalDescription,
        senderContext: 2,
        // Personal requests send no org; the backend rejects orgs the sender is not an active member of
        senderOrganizationId: typeof organizationId === "number" ? organizationId : undefined,
        receiverContext: 2,
        receiverOrganizationId: typeof organizationId === "number" ? organizationId : undefined,
        input_field_values: inputFieldValues,
        assigneeIds: verifierIds,
      };

      RemoteLogger.info("[Verification] 🚀 Creating verification request...", payload);
      const response = await VerificationService.create(payload, token);
      RemoteLogger.info("[Verification] 📥 Response received:", response);

      if (response.data) {
        setStatus("requested");
        // The real data is nested inside response.data.data
        const actualData = (response.data as any).data;
        if (actualData && actualData.id) {
          setLastVerificationId(actualData.id);
        }
      } else {
        if (response.status === 401) {
          RemoteLogger.warn("Session expired (401), logging out...");
          onLogout();
        } else {
          setErrorMessage(response.error || "Failed to create verification request");
          setStatus("none");
        }
      }
    } catch (error: any) {
      RemoteLogger.error("Verification error:", error);
      setErrorMessage(error?.message || "An error occurred while requesting verification.");
      setStatus("none");
    }
  };

  useEffect(() => {
    loadEmailDetails();

    const handleItemChanged = () => {
      const date = new Date();
      const formattedDate = date.toISOString();
      setCurrentTime(formattedDate);

      RemoteLogger.info("handleItemChanged triggered:", formattedDate);

      setStatus("none");
      setLastVerificationId(null);
      setErrorMessage(null);
      // Wait a short delay to ensure the Outlook host has fully synchronized Office.context.mailbox.item
      setTimeout(() => {
        loadEmailDetails();
      }, 200);
    };
    RemoteLogger.info("triggered");
    Office.context?.mailbox?.addHandlerAsync(Office.EventType.ItemChanged, handleItemChanged);

    return () => {
      Office.context?.mailbox?.removeHandlerAsync(Office.EventType.ItemChanged, handleItemChanged);
    };
  }, []);

  if (status === "select-verifier") {
    return (
      <SelectVerifier
        emailSummary={{
          fromOrTo: `From: ${emailDetails?.sender || "Unknown Sender"}`,
          subject: emailDetails?.subject || "No Subject",
          attachments: emailDetails?.attachments,
        }}
        onSelect={handleRequestVerification}
        onBack={() => setStatus("none")}
        onLogout={onLogout}
      />
    );
  }
  const initials = emailDetails?.sender
    ? emailDetails.sender
        .split(" ")
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  return (
    <div className="flex flex-col w-full bg-white">
      {/* Header */}

      <Header title="Email Verification" />
      <button
        aria-label="Back"
        className="text-gray-500 hover:text-gray-700 mt-5 px-4"
        onClick={() => onBack()}
      >
        <IoArrowBackOutline size={16} />
      </button>

      {errorMessage && (
        <div className="mx-4 mt-3 p-3 bg-red-50 border border-red-200 text-red-700 text-xs font-semibold rounded-xl flex items-center justify-between animate-fade-in">
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4 text-red-500 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="break-words max-w-[200px]">{errorMessage}</span>
          </div>
          <button
            type="button"
            aria-label="Dismiss error message"
            title="Dismiss"
            onClick={() => setErrorMessage(null)}
            className="text-red-500 hover:text-red-700 p-0.5 rounded-full hover:bg-red-100/50 transition-colors flex-shrink-0"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Dashboard Body */}
      <main className="flex-1 px-4 py-4">
        {emailDetails ? (
          <div className="flex flex-col gap-3">
            {/* From */}
            <div className=" rounded-2xl bg-gray-50 p-5">
              <div className="flex items-center h-20 gap-2  ">
                <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center text-[12px] font-semibold flex-shrink-0">
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-gray-900 truncate">
                    {emailDetails.sender}
                  </div>
                  <div className="text-[11px] text-gray-400 truncate">
                    {emailDetails.senderAddress}
                  </div>
                </div>
                <button
                  onClick={loadEmailDetails}
                  disabled={loadingEmail}
                  className="border border-gray-200 rounded-md px-2 py-1 text-[11px] text-gray-500 bg-transparent hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  {loadingEmail ? "..." : "↺"}
                </button>
              </div>

              <hr className="border border-gray-200" />

              {/* Subject */}
              <div className="my-5">
                <span id="item-subject" className="text-sm text-gray-800  break-words font-medium">
                  {emailDetails.subject}
                </span>
              </div>

              {/* Attachments Section */}
              {emailDetails.attachments && emailDetails.attachments.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2">
                    Attachments ({emailDetails.attachments.length})
                  </span>
                  <div className="flex flex-col gap-2">
                    {emailDetails.attachments.map((att) => (
                      <div
                        key={att.id}
                        className="flex items-center gap-2 p-2 bg-white border border-gray-100 rounded-xl hover:border-[#33ccbf] transition-colors"
                      >
                        <IoAttachOutline className="text-gray-400 shrink-0" size={16} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-700 truncate">{att.name}</p>
                          <p className="text-[10px] text-gray-400 font-medium">
                            {formatBytes(att.size)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {/* Preview */}
            {/* <div>
              <span className="block text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">
                Preview
              </span>
              <div
                id="item-body"
                className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-xl p-2 max-h-36 overflow-y-auto leading-relaxed break-words"
              >
                {emailDetails.bodyPreview}
              </div>
            </div> */}

            {/* Verification Section */}
            <div className="mt-4  g">
              <div className="flex flex-col gap-2">
                {status === "none" || status === "loading" ? (
                  <>
                    <div className="flex items-center gap-1.5 px-2.5 py-2 bg-gray-100 rounded-lg mt-2 text-gray-500 w-full">
                      <IoAlertCircleOutline />
                      <span className="text-[12px] font-semibold">
                        No verification on this email yet
                      </span>
                    </div>

                    <button
                      disabled={status === "loading"}
                      onClick={() => setStatus("select-verifier")}
                      className="w-full mt-2.5 py-3 bg-[#33ccbf] hover:bg-[#2bbdb1] text-white border-none rounded-lg text-[14px] font-bold flex items-center justify-center gap-1.5 transition-colors active:scale-[0.98] cursor-pointer"
                    >
                      <IoShieldOutline size={18} color="white" />
                      {status === "loading" ? "Requesting..." : "Request Verification"}
                    </button>
                  </>
                ) : (
                  <div className="text-center py-2">
                    <div className="inline-flex items-center justify-center w-8 h-8 bg-[#47DED1]/10 rounded-full mb-2">
                      <svg
                        className="w-5 h-5 text-[#33ccbf]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <h3 className="text-sm font-bold text-[#33ccbf]">Verification Requested</h3>
                    <p className="text-[11px] text-[#47DED1] mt-1">
                      We've notified your trusted contacts.
                    </p>
                    {lastVerificationId && (
                      <button
                        onClick={onViewHistory}
                        className="mt-3 text-[11px] font-bold text-[#33ccbf] bg-blue-50 px-3 py-1.5 rounded-full hover:bg-[#47DED1] hover:text-white transition-colors"
                      >
                        View in History
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-xs text-gray-400 text-center py-8">
            {loadingEmail
              ? "Loading email data..."
              : "No email selected or not running inside Outlook."}
          </p>
        )}
      </main>
    </div>
  );
};

export default VerifyEmailView;
